import * as THREE from "three";

export type LoungeChair1BackrestProfile = {
  footprintDepth: number;
  taperDepth: number;
  topAngleDrop: number;
};

export type LoungeChair1ShellGeometryOptions = {
  width: number;
  depth: number;
  height: number;
  armHeight: number;
  shellThickness: number;
  straightSegments?: number;
  backSegments?: number;
  railSegments?: number;
};

type LoungeChair1PathSample = {
  x: number;
  y: number;
  normalX: number;
  normalY: number;
  crownHeight: number;
};

const DEFAULT_STRAIGHT_SEGMENTS = 14;
const DEFAULT_BACK_SEGMENTS = 40;
const DEFAULT_RAIL_SEGMENTS = 10;
const DEFAULT_TIP_SEGMENTS = 6;

function smoothstep01(value: number): number {
  const clamped = THREE.MathUtils.clamp(value, 0, 1);
  return clamped * clamped * (3 - 2 * clamped);
}

/**
 * Builds the lounge chair's upholstered tub as one continuous swept shell.
 *
 * Its centreline runs from the left arm tip, around a semicircular back, and
 * down to the right arm tip. A closed, softly rounded cross-section is swept
 * along that path. This avoids the large triangulated top face that made the
 * old extruded primitive look faceted, while preserving the symbol's exact
 * outer width and depth.
 */
export function createLoungeChair1ShellBufferGeometry({
  width,
  depth,
  height,
  armHeight,
  shellThickness,
  straightSegments = DEFAULT_STRAIGHT_SEGMENTS,
  backSegments = DEFAULT_BACK_SEGMENTS,
  railSegments = DEFAULT_RAIL_SEGMENTS,
}: LoungeChair1ShellGeometryOptions): THREE.BufferGeometry {
  const safeWidth = Math.max(width, 0.04);
  const safeDepth = Math.max(depth, 0.04);
  const safeThickness = THREE.MathUtils.clamp(
    shellThickness,
    0.035,
    safeWidth * 0.32,
  );
  const halfThickness = safeThickness / 2;
  const centrelineRadius = Math.max(
    safeWidth / 2 - halfThickness,
    halfThickness,
  );
  const frontY = -safeDepth / 2;
  const armStartY = frontY + halfThickness;
  const backCentreY = safeDepth / 2 - safeWidth / 2;
  const rearCrownY = backCentreY + centrelineRadius;
  const safeHeight = Math.max(height, 0.08);
  const safeArmHeight = THREE.MathUtils.clamp(
    armHeight,
    halfThickness * 1.5,
    safeHeight - 0.01,
  );
  const path: LoungeChair1PathSample[] = [];
  const crownHeightAtDepth = (y: number) =>
    THREE.MathUtils.lerp(
      safeArmHeight,
      safeHeight,
      smoothstep01((y - frontY) / Math.max(rearCrownY - frontY, 0.01)),
    );

  for (let index = 0; index <= straightSegments; index += 1) {
    const progress = index / straightSegments;
    path.push({
      x: -centrelineRadius,
      y: THREE.MathUtils.lerp(armStartY, backCentreY, progress),
      normalX: -1,
      normalY: 0,
      crownHeight: crownHeightAtDepth(
        THREE.MathUtils.lerp(armStartY, backCentreY, progress),
      ),
    });
  }

  for (let index = 1; index <= backSegments; index += 1) {
    const angle = Math.PI * (1 - index / backSegments);
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    path.push({
      x: centrelineRadius * cos,
      y: backCentreY + centrelineRadius * sin,
      normalX: cos,
      normalY: sin,
      crownHeight: crownHeightAtDepth(
        backCentreY + centrelineRadius * sin,
      ),
    });
  }

  for (let index = 1; index <= straightSegments; index += 1) {
    const progress = index / straightSegments;
    path.push({
      x: centrelineRadius,
      y: THREE.MathUtils.lerp(backCentreY, armStartY, progress),
      normalX: 1,
      normalY: 0,
      crownHeight: crownHeightAtDepth(
        THREE.MathUtils.lerp(backCentreY, armStartY, progress),
      ),
    });
  }

  const railRise = Math.min(safeThickness * 0.42, 0.042);
  const lowerEdgeRadius = Math.min(safeThickness * 0.16, 0.016);
  const crossSections = path.map((sample) => {
    const sideTop = Math.max(sample.crownHeight - railRise, lowerEdgeRadius);
    const section: Array<[number, number]> = [
      [halfThickness - lowerEdgeRadius, 0],
      [halfThickness, lowerEdgeRadius],
      [halfThickness, sideTop],
    ];

    for (let index = 1; index < railSegments; index += 1) {
      const angle = (Math.PI * index) / railSegments;
      section.push([
        halfThickness * Math.cos(angle),
        sideTop + railRise * Math.sin(angle),
      ]);
    }

    section.push(
      [-halfThickness, sideTop],
      [-halfThickness, lowerEdgeRadius],
      [-halfThickness + lowerEdgeRadius, 0],
    );
    return section;
  });

  const vertices: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const sectionSize = crossSections[0].length;

  /** Arc-length U so fabric/stone albedo stretches evenly along the tub. */
  const pathDistances: number[] = [0];
  for (let index = 1; index < path.length; index += 1) {
    pathDistances.push(
      pathDistances[index - 1] +
        Math.hypot(
          path[index].x - path[index - 1].x,
          path[index].y - path[index - 1].y,
        ),
    );
  }
  const totalPathLength = Math.max(
    pathDistances[pathDistances.length - 1],
    1e-6,
  );

  path.forEach((sample, pathIndex) => {
    const u = pathDistances[pathIndex] / totalPathLength;
    crossSections[pathIndex].forEach(([normalOffset, z], sectionIndex) => {
      vertices.push(
        sample.x + sample.normalX * normalOffset,
        sample.y + sample.normalY * normalOffset,
        z,
      );
      uvs.push(u, sectionIndex / sectionSize);
    });
  });

  for (let pathIndex = 0; pathIndex < path.length - 1; pathIndex += 1) {
    for (let sectionIndex = 0; sectionIndex < sectionSize; sectionIndex += 1) {
      const nextSectionIndex = (sectionIndex + 1) % sectionSize;
      const current = pathIndex * sectionSize + sectionIndex;
      const currentNext = pathIndex * sectionSize + nextSectionIndex;
      const following = (pathIndex + 1) * sectionSize + sectionIndex;
      const followingNext =
        (pathIndex + 1) * sectionSize + nextSectionIndex;
      indices.push(current, following, currentNext);
      indices.push(currentNext, following, followingNext);
    }
  }

  const addRoundedEndCap = (pathIndex: number, reverse: boolean) => {
    const sample = path[pathIndex];
    const section = crossSections[pathIndex];
    const tipU = pathDistances[pathIndex] / totalPathLength;
    let previousRingStart = pathIndex * sectionSize;

    for (
      let ringIndex = 1;
      ringIndex <= DEFAULT_TIP_SEGMENTS;
      ringIndex += 1
    ) {
      const progress = ringIndex / DEFAULT_TIP_SEGMENTS;
      const angle = progress * (Math.PI / 2);
      const ringScale = Math.cos(angle);
      const forwardOffset = halfThickness * Math.sin(angle);
      const ringStart = vertices.length / 3;

      section.forEach(([normalOffset, z], sectionIndex) => {
        vertices.push(
          sample.x + sample.normalX * normalOffset * ringScale,
          armStartY - forwardOffset,
          z,
        );
        uvs.push(tipU, sectionIndex / sectionSize);
      });

      for (
        let sectionIndex = 0;
        sectionIndex < sectionSize;
        sectionIndex += 1
      ) {
        const nextSectionIndex = (sectionIndex + 1) % sectionSize;
        const current = previousRingStart + sectionIndex;
        const currentNext = previousRingStart + nextSectionIndex;
        const following = ringStart + sectionIndex;
        const followingNext = ringStart + nextSectionIndex;
        if (reverse) {
          indices.push(current, currentNext, following);
          indices.push(currentNext, followingNext, following);
        } else {
          indices.push(current, following, currentNext);
          indices.push(currentNext, following, followingNext);
        }
      }

      previousRingStart = ringStart;
    }
  };

  addRoundedEndCap(0, true);
  addRoundedEndCap(path.length - 1, false);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(vertices, 3),
  );
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

/** Depth fraction (measured from the front edge) where the backrest begins. */
const BACKREST_START_DEPTH_RATIO = 0.04;
/** Depth fraction where the backrest is fully formed. */
const BACKREST_FULL_DEPTH_RATIO = 0.24;
/** Depth ratios across which the top edge sweeps up from the arm tips. */
const SWEEP_START_DEPTH_RATIO = 0.06;
const SWEEP_END_DEPTH_RATIO = 0.62;
/** Height ratio where the shell starts pulling in toward the backrest crown. */
const TAPER_START_HEIGHT_RATIO = 0.26;
/** Height ratio where the crown starts rolling over into the seat opening. */
const CROWN_START_HEIGHT_RATIO = 0.34;
/** Share of the sweep drop reused to roll the crown over the backrest top. */
const CROWN_DROP_RATIO = 0.4;
/** Spreads the crown roll across the backrest thickness instead of one edge. */
const CROWN_SPREAD_EXPONENT = 0.75;
/** Lowest share of the shell height the arm tips may keep. */
const MIN_ARM_HEIGHT_RATIO = 0.34;
/** Keeps the tapered front face from crossing the backrest's rear face. */
const MAX_TAPER_HEADROOM_RATIO = 0.68;

function smoothstep(min: number, max: number, value: number): number {
  const normalized = THREE.MathUtils.clamp((value - min) / (max - min), 0, 1);
  return normalized * normalized * (3 - 2 * normalized);
}

/**
 * Shapes the upper half of lounge chair 1's shell: the top edge sweeps down
 * from the backrest toward the arm tips, the backrest wall thins on the way up
 * and its crown rolls over instead of ending in a flat cap - the same read as
 * lounge chair 2's wrapper. The outline at floor level stays fixed so the 2D
 * footprint and placed dimensions do not change.
 */
export function applyLoungeChair1BackrestProfile(
  geometry: THREE.BufferGeometry,
  extrudeDepth: number,
  profile: LoungeChair1BackrestProfile,
): void {
  const position = geometry.attributes.position;
  const rearEdgeY = profile.footprintDepth / 2;
  const frontEdgeY = -rearEdgeY;
  const profileStartY = profile.footprintDepth * BACKREST_START_DEPTH_RATIO;
  const fullProfileY = profile.footprintDepth * BACKREST_FULL_DEPTH_RATIO;
  const armHeightRatio = Math.max(
    (extrudeDepth - profile.topAngleDrop) / extrudeDepth,
    MIN_ARM_HEIGHT_RATIO,
  );

  for (let index = 0; index < position.count; index += 1) {
    const originalY = position.getY(index);
    const originalZ = position.getZ(index);
    const heightRatio = THREE.MathUtils.clamp(originalZ / extrudeDepth, 0, 1);
    const depthRatio = THREE.MathUtils.clamp(
      (originalY - frontEdgeY) / profile.footprintDepth,
      0,
      1,
    );

    const sweep = smoothstep(
      SWEEP_START_DEPTH_RATIO,
      SWEEP_END_DEPTH_RATIO,
      depthRatio,
    );
    let z = originalZ * THREE.MathUtils.lerp(armHeightRatio, 1, sweep);

    const backrestFactor = smoothstep(profileStartY, fullProfileY, originalY);
    if (backrestFactor > 0) {
      const taperFactor = smoothstep(TAPER_START_HEIGHT_RATIO, 1, heightRatio);
      const rearHeadroom = Math.max(rearEdgeY - originalY, 0);
      const taperShift = Math.min(
        profile.taperDepth * backrestFactor * taperFactor,
        rearHeadroom * MAX_TAPER_HEADROOM_RATIO,
      );
      position.setY(index, originalY + taperShift);

      const crownFactor = smoothstep(CROWN_START_HEIGHT_RATIO, 1, heightRatio);
      const innerFaceFactor = 1 - smoothstep(fullProfileY, rearEdgeY, originalY);
      z -=
        profile.topAngleDrop *
        CROWN_DROP_RATIO *
        backrestFactor *
        crownFactor *
        Math.pow(innerFaceFactor, CROWN_SPREAD_EXPONENT);
    }

    position.setZ(index, z);
  }

  position.needsUpdate = true;
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}
